import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { z } from 'zod';
import { CategoryEnumSchema } from './models.categories';
import { BooleanStrict, GenericUserID } from './models.generic';
import {
  CreateMemoryAPISchema,
  MemoryAgentSchema,
  MemoryContextSchema,
  MemorySourceEnumSchema,
} from './models.memories';

extendZodWithOpenApi(z);

// Type Exports
export type TrackerMemory = z.infer<typeof TrackerMemoryRawSchema>; // NB! We expect these to diverge over time, so we're keeping that door open (but not doing until needed)
export type TrackerMemoryParsed = z.infer<typeof TrackerMemoryParsedSchema>;
/** @deprecated Naming is terrible; do the renaming ref schema definition further down */
export type MemoryToolConfig = z.infer<typeof ToolConfigurationDefinitionSchema>;
export type MemoryTitlePattern = z.infer<typeof PatternTitleSchema>;
export type MemoryURLPattern = z.infer<typeof PatternURLSchema>;

/* Metadata from Tracker to store on Memory Uploads */
export type TrackerUploadMeta = z.infer<typeof TrackerUploadMetaSchema>;
export const TrackerUploadMetaSchema = z.object({
  user_id: GenericUserID,
  source: z.string().openapi({ example: 'mac', description: 'Source of the Memory Tracker' }),
  device_hostname: z.string().openapi({
    description: 'The unique device hostname.',
    example: 'some-unique-hostname',
  }),
  device_id: z.string().uuid().openapi({
    description: 'The unique device id.',
    example: '23b3b3b3-3b3b-3b3b-3b3b-3b3b3b3b3b3b',
  }),
  last_seen_at: z.string().datetime({ offset: true }),
});

/**
 * `memory_tracker_meta` records, presented as "tracker sessions"
 */
export type PresentMemoryTrackerMeta = z.infer<typeof PresentMemoryTrackerMetaSchema>;
export const PresentMemoryTrackerMetaSchema = TrackerUploadMetaSchema.omit({
  user_id: true,
  device_id: true,
}).extend({
  first_seen_at: z.string().datetime({ offset: true }),
  notified_via: z.array(z.enum(['email', 'in_app'])).optional(),
});

/**
 * TrackerMemoryRaw, directly from Tracker
 */

export const TrackerMemoryRawSchema = CreateMemoryAPISchema.pick({
  title: true,
  started_at: true,
  app: true,
  context: true,
})
  .extend({
    url: z
      .string()
      // This broke local dev, due to `url: ""` being sent.
      // See https://linear.app/timelyapp/issue/TT-544/[tauritracker]-never-send-memory-with-url
      // .url()
      .nullable()
      .openapi({
        description: 'URL or file://path of this Memory',
        example: 'https://github.com/Timely/app',
      })
      .optional(),
    ended_at: z
      .string()
      .datetime({ offset: true })
      .openapi({ example: '2021-10-10T10:23:33.000Z' }),
    /**
     * We _may_ want to _flag_ the Memory as ignored from Memory Tracker, but still allow it to be uploaded?
     * @note Not totally sure if this is the best place for this type, but pretty sure it should be applied before parsing happens so that rewrites++ should be done before parsing happens.
     */
    ignored: z.boolean().default(false).optional(),
    agent: MemoryAgentSchema.optional(),
  })
  .strict();

/**
 * TrackerMemoryParsed, from TrackerMemory
 */

export const TrackerMemoryParsedSchema = TrackerMemoryRawSchema.pick({
  title: true,
  started_at: true,
  ended_at: true,
  url: true,
  ignored: true,
})
  .extend({
    context: MemoryContextSchema.optional(),
    tool_id: z.string().optional(),
    tool_raw: z.string().optional(),
    window_tool_raw: z.string().optional(),
    conflict_key: z.string().optional(),
    uri: z.string().url().optional(),
    tracker_source: MemorySourceEnumSchema.nullable(),
    agent: MemoryAgentSchema.optional(),
    /**
     * Reference URIs for memory-to-resource linking.
     * Computed from URL pattern matches with hierarchy metadata (level + parentUri).
     * @see MemoryRefsSchema
     */
    refs: z.lazy(() => MemoryRefsSchema).optional(),
  })
  .strict();
/**
 * Tool Configurations
 */

const PatternRegexSchema = z.string().openapi({
  example: '(.+) · Pull Request #([0-9]+) · (.+)/(.+)',
});

const PatternMapsToSchema = z.array(z.string()).openapi({
  example: ['prTitle', 'prNumber', 'owner', 'repo'],
});
const PatternTitleRewriteSchema = z.string().openapi({
  example: '{prTitle} (#{prNumber})',
});

const PatternTitleSchema = z
  .object({
    in: z.enum(['title']),
    pattern: PatternRegexSchema,
    mapsTo: PatternMapsToSchema,
    niceTitle: PatternTitleRewriteSchema,
    context: MemoryContextSchema.optional(),
  })
  .strict();

const PatternURLSchema = z
  .object({
    in: z.enum(['url']),
    pattern: PatternRegexSchema,
    mapsTo: PatternMapsToSchema,
    uri: z
      .string()
      .url()
      .openapi({
        description:
          'URI, as understood by TIC, allows us to re-format URLs into unique identifiers that represent API endpoints. ',
      })
      .optional(),
    fallbackTitle: PatternTitleRewriteSchema.optional(),
    context: MemoryContextSchema.optional(),
    /**
     * Hierarchy level of the matched resource.
     * - 'task': Leaf-level work items (PRs, issues, cards, tickets)
     * - 'project': Container resources (repos, boards, projects)
     * - 'org': Organization/workspace level (reserved for future use)
     */
    level: z.enum(['task', 'project', 'org']).optional().openapi({
      description: 'Hierarchy level of the matched resource',
      example: 'task',
    }),
    /**
     * URI template for the parent resource.
     * Uses same {placeholder} syntax as `uri`, referencing values from `mapsTo`.
     * @example For a PR pattern, parentUri might be 'https://api.github.com/repos/{owner}/{repo}'
     */
    parentUri: z.string().optional().openapi({
      description: 'URI template for the parent resource (e.g., repo URI for a PR)',
      example: 'https://api.github.com/repos/{owner}/{repo}',
    }),
  })
  .strict();

/**
 * Reference URIs linking a memory to its hierarchical context (task/project).
 * Populated during memory parsing when URL patterns match hierarchy metadata.
 * Stored in memories_partition.refs JSONB column for efficient read-time access.
 */
export const MemoryRefsSchema = z
  .object({
    task_uri: z.string().optional().openapi({
      description: 'URI of the task-level resource this memory relates to (e.g., PR, issue, card)',
      example: 'https://api.github.com/repos/Timely/app/pulls/123',
    }),
    project_uri: z.string().optional().openapi({
      description: 'URI of the project-level resource this memory relates to (e.g., repo, board)',
      example: 'https://api.github.com/repos/Timely/app',
    }),
  })
  .strict();
export type MemoryRefs = z.infer<typeof MemoryRefsSchema>;

/** @todo Rename this + type export + `tool-configurations.ts` to not be conflicting with the general "tool_configs"/ToolConfig concept*/
const ToolConfigurationDefinitionSchema = z
  .object({
    tool: z
      .object({
        id: z
          .string()
          .optional()
          .describe(
            'The `tool-configuration.ts` key, embedded into payload via `prepareToolMaps` to allow Tracker-originating memories to reference ID directly'
          ),
        /** End-title of the Memory, re-written from `aliases` */
        title: z.string(),
        category: CategoryEnumSchema.optional(),
        color: z
          .string()
          .nullable()
          .openapi({ description: 'CSS color to use for Memories of this tool' })
          .default('rgba(0,0,0,0.30)')
          .optional(),
        icon: z.string().optional().openapi({
          description:
            'Icon to use for Memories of this tool. Add file to apps/integration-cloud/public/images/tools/[tool key].png',
        }),
        /** Aliases (e.g, Photoshop CS, Photoshop Creative Cloud) */
        aliases: z
          .array(z.string())
          .openapi({
            description: 'Aliases the tool is known by',
            example: [
              'Photoshop CC',
              'Adobe Photoshop',
              'Adobe Photoshop CC',
              'Adobe Photoshop 2020',
              'Adobe Photoshop 2021',
            ],
          })
          .optional(),
        /**
         * Rename the title of the Memory to something else.
         * @example `true`, will rename matching "aliases" to the "title" of the tool
         * @example `[{title: 'old title', newTitle: 'new title'}]`, will rename "old title" to "new title"
         * @note If array is supplied but no matches found, it'll defer to the next title constructor (ref `constructConfiguredMemory`)
         */
        coerceTitle: z
          .boolean()
          .default(false)
          .or(
            z.array(
              z
                .object({
                  title: z.string(),
                  newTitle: z.string(),
                })
                .strict()
            )
          )
          .optional(),
        /** Include all domains we should attribute to this app */
        domains: z
          .array(z.string())
          .openapi({
            description: 'Domains the tool hosted on. Wildcard supported',
            example: ['photoshop.com', '*.adobe.com'],
          })
          .optional(),
        /** Strips query params and fragments from the URL, defaults to `true` */
        cleanUrls: z
          .boolean()
          .default(true)
          .openapi({
            description: 'Strips query params and fragments from the URL',
          })
          .optional(),
      })
      .strict(),
    /** IMPORTANT: order the patterns starting with the MOST to LEAST specific */
    patterns: z.array(PatternTitleSchema.or(PatternURLSchema)).optional().nullable(),
  })
  .strict();

export const PostCreateMemoriesPayloadSchema = z.object({
  tracker_source: MemorySourceEnumSchema,
  device_id: z.string().optional().openapi({
    description: 'The unique device id.',
    example: 'some-unique-identifier',
  }),
  device_hostname: z.string().optional().openapi({
    description: 'The unique device id.',
    example: 'some-unique-hostname',
  }),
  memories: z.array(TrackerMemoryRawSchema),
  more_batches_available: BooleanStrict.optional().default(false).openapi({
    description:
      'If true, more batches are available to be uploaded (so "new memories!" Pusher dispatch should not run yet)',
    example: true,
  }),
});
